// INC2-CONTACT: Work › Contacts (`/c/{slug}/work/contacts`; build order increment 2 C) — the thin
// contact core as a plain list and nothing more: name, email, phone, organisation, stage, DND.
// Contacts arrive from bookings, forms, imports and the GHL sync (the upsert); the CRM's record
// page, edits and merges come with its phase 1. Search runs on the server (name, email, phone,
// organisation). Calls on load: `contacts.list` (one page; "Load more" pages on).
import type { OperationOutput } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  Input,
  PageHeader,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ContactRoundIcon, SearchIcon } from "lucide-react";
import { useDeferredValue, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDate } from "../format";
import { LoadError, Loading } from "../screen-parts";

export type ContactRow = OperationOutput<"contacts.list">["data"][number];

const LIFECYCLE: Record<ContactRow["lifecycle"], string> = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer",
  past: "Past",
  other: "Other",
};

export const contactsKey = (co: string) => ["company", co, "contacts"] as const;

export function ContactsScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const [search, setSearch] = useState("");
  const q = useDeferredValue(search.trim());
  const list = useInfiniteQuery({
    queryKey: [...contactsKey(co), q],
    queryFn: ({ pageParam }) =>
      api.client.contacts.list({
        params: { co },
        query: { limit: 50, ...(q ? { q } : {}), ...(pageParam ? { cursor: pageParam } : {}) },
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.next_cursor,
    placeholderData: keepPreviousData,
  });
  const rows = (list.data?.pages ?? []).flatMap((p) => p.data);
  return (
    <div className="flex min-w-0 flex-col gap-4 px-6 py-5 max-md:px-4" data-slot="contacts">
      <PageHeader
        eyebrow="Work"
        title="Contacts"
        badge={
          <InfoTip label="About contacts">
            People outside the company, one record each: a booking, a form, an import or the GHL
            sync finds them by email, phone or their GHL id before adding anyone.
          </InfoTip>
        }
      />
      <div className="relative max-w-sm">
        <SearchIcon
          aria-hidden="true"
          className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          aria-label="Search contacts"
          placeholder="Search"
          className="pl-8"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {list.isPending ? (
        <Loading label="Loading contacts" rows={5} />
      ) : list.isError ? (
        <LoadError what="Contacts" error={list.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ContactRoundIcon}
          title={q ? "No contact matches" : "No contacts yet"}
          description={
            q
              ? "Try a name, an email, a phone or an organisation."
              : "Bookings, forms, imports and the GHL sync add them."
          }
        />
      ) : (
        <Table aria-label="Contacts" data-slot="contacts-table">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Name</TableHead>
              <TableHead scope="col">Email</TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Phone
              </TableHead>
              <TableHead scope="col" className="max-md:hidden">
                Organisation
              </TableHead>
              <TableHead scope="col">Stage</TableHead>
              <TableHead scope="col" className="max-lg:hidden">
                Added
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id} data-contact={c.id}>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {c.display_name}
                    {c.dnd ? (
                      <Badge variant="warning" title="Opted out of a channel">
                        DND
                      </Badge>
                    ) : null}
                  </span>
                </TableCell>
                <TableCell className="font-mono text-[13px]">{c.email ?? "—"}</TableCell>
                <TableCell className="font-mono text-[13px] max-md:hidden">
                  {c.phone ?? "—"}
                </TableCell>
                <TableCell className="max-md:hidden">{c.organisation_name ?? "—"}</TableCell>
                <TableCell>
                  <Badge variant="outline">{LIFECYCLE[c.lifecycle]}</Badge>
                </TableCell>
                <TableCell className="text-muted-foreground tabular-nums max-lg:hidden">
                  {formatDate(c.created_at)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      {list.hasNextPage ? (
        <Button
          variant="ghost"
          size="sm"
          className="self-start"
          disabled={list.isFetchingNextPage}
          onClick={() => void list.fetchNextPage()}
        >
          Load more
        </Button>
      ) : null}
    </div>
  );
}
